import {
  type InputData,
  type RecordJobData,
  type ValueData,
  unwrapValueData,
} from "@smartsuite/automation-engine-api";
import {
  valueDataToString,
  valueDataToStringsWithDelimenters,
} from "@smartsuite/automation-conditions";
import type { JobDetails } from "../ActivityDetails.types";
import { getInputValue } from "../GeneralActionResultGroup/utils";

export const MS_TEAMS_SEND_ACTIONABLE_NOTIFICATION_ACTION_ID =
  "ms-teams-send-actionable-notification";
export const MS_TEAMS_ACTION_SEND_MESSAGE_ID = "ms-teams-send-message";

const MS_TEAMS_INPUT_ACTION_BUTTONS_ID = "ms-teams-input-action-buttons";
const MS_TEAMS_INPUT_MESSAGE_TYPE_ID = "ms-teams-input-message-type";
const MS_TEAMS_INPUT_PERSON_ID = "ms-teams-input-person";
const MS_TEAMS_INPUT_TEAM_ID = "ms-teams-input-team";
const MS_TEAMS_INPUT_CHANNEL_ID = "ms-teams-input-channel";
const MS_TEAMS_INDIVIDUAL_MESSAGE_TYPE = "individual";

const getResolvedInputValue = (
  input: InputData,
  recordJob?: RecordJobData,
): ValueData | undefined => {
  const output =
    recordJob?.outputData?.recordList?.recordList !== undefined
      ? recordJob.outputData.recordList.recordList.records[0]?.outputs.find(
          (currentOutput) => currentOutput.outputId === input.inputId,
        )
      : undefined;

  return output?.value ?? input.value;
};

const getResolvedInputStringValue = (
  input: InputData | undefined,
  jobDetails: JobDetails,
  recordJob?: RecordJobData,
): string => {
  if (input === undefined) {
    return "";
  }

  const value = getResolvedInputValue(input, recordJob);
  if (value === undefined) {
    return "";
  }

  return valueDataToString(value, jobDetails.job.automation?.timezone).trim();
};

export const getMsTeamsActionableNotificationInputValue = (
  input: InputData,
  jobDetails: JobDetails,
  recordJob?: RecordJobData,
): string => {
  if (input.inputId !== MS_TEAMS_INPUT_ACTION_BUTTONS_ID) {
    return getInputValue(input, jobDetails, recordJob);
  }

  const value = getResolvedInputValue(input, recordJob);
  if (value === undefined) {
    return "";
  }

  const labels = unwrapValueData(value).values.flatMap((currentValue) => {
    if (currentValue.value.oneofKind !== "keyValue") {
      return [];
    }

    const label = valueDataToString(
      currentValue.value.keyValue.value,
      jobDetails.job.automation?.timezone,
    ).trim();

    return label !== "" ? [label] : [];
  });

  if (labels.length > 0) {
    return labels.join("; ");
  }

  return valueDataToStringsWithDelimenters(
    value,
    jobDetails.job.automation?.timezone,
  ).join("; ");
};

export const shouldDisplayMsTeamsActionableNotificationInput = (
  input: InputData,
  jobDetails: JobDetails,
  recordJob?: RecordJobData,
): boolean => {
  if (recordJob === undefined) {
    return true;
  }

  if (input.inputId === MS_TEAMS_INPUT_MESSAGE_TYPE_ID) {
    return true;
  }

  if (
    ![
      MS_TEAMS_INPUT_PERSON_ID,
      MS_TEAMS_INPUT_TEAM_ID,
      MS_TEAMS_INPUT_CHANNEL_ID,
    ].includes(input.inputId)
  ) {
    return true;
  }

  const messageType = getResolvedInputStringValue(
    recordJob.inputs.find(
      (currentInput) => currentInput.inputId === MS_TEAMS_INPUT_MESSAGE_TYPE_ID,
    ),
    jobDetails,
    recordJob,
  );

  if (messageType === MS_TEAMS_INDIVIDUAL_MESSAGE_TYPE) {
    return input.inputId === MS_TEAMS_INPUT_PERSON_ID;
  }

  return (
    input.inputId === MS_TEAMS_INPUT_TEAM_ID ||
    input.inputId === MS_TEAMS_INPUT_CHANNEL_ID
  );
};
